import type { History } from "history";

import { navigateToRecord, recordHome } from "src/api/records";

import type { SourceProvenanceReference } from "../../services/ArtifactService";
import { manifestPageTarget } from "../../utils/manifestPage";
import { buildManifestAnnotations } from "../sources/buildDocumentAnnotations";

/**
 * Whether a generated-doc provenance reference can be opened in the document
 * viewer — it needs a source data record plus a resolvable scope: a dataset
 * OR a project folder (#2281; the two are mutually exclusive by construction,
 * `ck_sdr_scope_xor`). Redacted refs (a reviewer who can't see the source)
 * blank all of these, so they stay inert rather than pushing a broken route
 * (#20423 Story 5 / C3).
 */
export const canOpenArtifactRef = (
  ref: Pick<
    SourceProvenanceReference,
    "dataset_id" | "source_data_record_id" | "project_folder_id"
  >,
): boolean =>
  ref.source_data_record_id != null &&
  (ref.dataset_id != null || ref.project_folder_id != null);

/**
 * Open a generated document's source passage in the annotated document viewer,
 * deep-linking exactly like an inline citation [N] does, and carrying every
 * manifest reference pointing at the same document as router location state
 * so all of that document's cited passages highlight together (#20423 Story 5).
 *
 * Shared by the "FROM [n]" reference list and the inline [n] markers rendered
 * in the generated-doc body, so both resolve to the identical view.
 */
export const openArtifactSource = (
  history: History,
  ref: SourceProvenanceReference,
  references: SourceProvenanceReference[],
): void => {
  if (!canOpenArtifactRef(ref)) return;
  const home = recordHome({
    dataset_fk: ref.dataset_id,
    project_folder_fk: ref.project_folder_id,
  });
  // First page of the span: both routes parse this with Number(), which yields
  // NaN for a comma-joined list and silently falls back to page 1.
  const pageTarget = manifestPageTarget(ref.page);
  const annotations = buildManifestAnnotations(
    references,
    ref.source_data_record_id as number,
  );
  navigateToRecord(history, home, ref.source_data_record_id as number, {
    chunkId: ref.chunk_id,
    page: pageTarget,
    annotations,
  });
};
